import { z } from 'zod';

/**
 * Schemas zod compartidos por todos los forms del admin. Espejean los
 * schemas del backend (apps/api/src/modules/*) — si cambia uno hay que
 * sincronizar el otro. A mediano plazo conviene moverlos a
 * `packages/shared-types` y consumirlos desde ambos.
 */

export const emailSchema = z.string().trim().email('Email inválido.');

export const loginSchema = z.object({
  email: emailSchema,
  password: z.string().min(1, 'La contraseña es obligatoria.'),
});
export type LoginInput = z.infer<typeof loginSchema>;

export const changePasswordSchema = z
  .object({
    current_password: z.string().min(1, 'Tu contraseña actual es obligatoria.'),
    new_password: z
      .string()
      .min(8, 'La nueva contraseña debe tener al menos 8 caracteres.')
      .max(200, 'Demasiado larga.'),
    confirm_password: z.string(),
  })
  .refine((d) => d.new_password === d.confirm_password, {
    message: 'La confirmación no coincide.',
    path: ['confirm_password'],
  })
  .refine((d) => d.current_password !== d.new_password, {
    message: 'La nueva contraseña debe ser distinta a la actual.',
    path: ['new_password'],
  });
export type ChangePasswordInput = z.infer<typeof changePasswordSchema>;

export const updateProfileSchema = z.object({
  name: z
    .string()
    .trim()
    .min(2, 'El nombre debe tener al menos 2 caracteres.')
    .max(150, 'Demasiado largo.'),
});
export type UpdateProfileInput = z.infer<typeof updateProfileSchema>;

export const roleSchema = z.enum(['admin', 'supervisor', 'agent']);

export const createUserSchema = z.object({
  email: emailSchema,
  name: z
    .string()
    .trim()
    .min(2, 'El nombre debe tener al menos 2 caracteres.')
    .max(150, 'Demasiado largo.'),
  role: roleSchema,
});
export type CreateUserInput = z.infer<typeof createUserSchema>;

export const editUserSchema = z.object({
  name: z
    .string()
    .trim()
    .min(2, 'El nombre debe tener al menos 2 caracteres.')
    .max(150, 'Demasiado largo.'),
  role: roleSchema,
});
export type EditUserInput = z.infer<typeof editUserSchema>;

export const prioritySchema = z.enum(['low', 'normal', 'high', 'urgent']);

export const createTicketSchema = z.object({
  subject: z
    .string()
    .trim()
    .min(3, 'El asunto debe tener al menos 3 caracteres.')
    .max(255, 'Demasiado largo.'),
  description: z.string().trim().min(1, 'La descripción es obligatoria.'),
  priority: prioritySchema,
  category: z.string().max(80).optional().or(z.literal('')),
  customer_name: z
    .string()
    .trim()
    .min(2, 'El nombre del cliente es obligatorio.')
    .max(150, 'Demasiado largo.'),
  customer_email: z
    .string()
    .trim()
    .email('Email inválido.')
    .optional()
    .or(z.literal('')),
  customer_phone: z.string().trim().max(50).optional().or(z.literal('')),
});
export type CreateTicketInput = z.infer<typeof createTicketSchema>;

export const apiKeyScopeSchema = z.enum([
  'tickets:create',
  'tickets:read',
  'tickets:update',
  'tickets:assign',
  'tickets:delete',
  'messages:create',
  'messages:read_internal',
  'users:manage',
  'api_keys:manage',
  'reports:read',
]);
export type ApiKeyScope = z.infer<typeof apiKeyScopeSchema>;

export const createApiKeySchema = z.object({
  name: z
    .string()
    .trim()
    .min(2, 'El nombre es obligatorio.')
    .max(150, 'Demasiado largo.'),
  platform: z
    .string()
    .trim()
    .min(2, 'Indicá una plataforma o proyecto.')
    .max(100, 'Demasiado largo.'),
  scopes: z
    .array(apiKeyScopeSchema)
    .min(1, 'Asigná al menos un permiso.'),
});
export type CreateApiKeyInput = z.infer<typeof createApiKeySchema>;

// === WhatsApp groups ===
export const whatsappGroupSchema = z.object({
  group_jid: z
    .string()
    .trim()
    .regex(/@g\.us$/i, 'El JID debe terminar en "@g.us" (ejemplo: 1203631234@g.us).')
    .max(190),
  name: z.string().trim().min(2, 'El nombre es obligatorio.').max(150),
  is_active: z.boolean().optional(),
});
export type WhatsappGroupInput = z.infer<typeof whatsappGroupSchema>;

export const updateWhatsappGroupSchema = z
  .object({
    name: z.string().trim().min(2).max(150).optional(),
    is_active: z.boolean().optional(),
  })
  .refine((d) => Object.keys(d).length > 0, {
    message: 'Indicá al menos un campo a actualizar.',
  });
export type UpdateWhatsappGroupInput = z.infer<typeof updateWhatsappGroupSchema>;
